import { createApi } from '@reduxjs/toolkit/query/react';
import { HttpMethod } from '@vision/ui/enums';
import { buildEndpointWithQuery } from '@vision/ui/utils';
import { ApiResponse, HttpBaseRequest, SchemaField } from '../../interfaces';
import { axiosBaseQuery } from '../base.service';
import {
  fromApiSchemaField,
  fromApiSchemaFieldList,
  fromApiSchemaFieldValues,
  toApiSaveSchemaField,
} from './schema-fields.mapper';
import type {
  CreateSchemaFieldArgs,
  GetSchemaFieldsV2Args,
  SchemaFieldDto,
  SchemaFieldValueOption,
  ToggleSchemaFieldArchiveArgs,
  UpdateSchemaFieldArgs,
} from './types';

const schemaFieldsTag = (accountId: string) => [{ type: 'SchemaFields' as const, id: accountId }];

export const SchemaFieldsService = createApi({
  reducerPath: 'SchemaFieldsService',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['SchemaFields'],
  endpoints: (builder) => ({
    getSchemaFields: builder.query<
      SchemaField[],
      HttpBaseRequest<{ accountId: string; query?: string; type?: string }>
    >({
      query: ({ accountId, query, type }) => ({
        url: buildEndpointWithQuery(
          `/v1/accounts/${accountId}/schema_fields`,
          [query, type ? `type=${encodeURIComponent(type)}` : ''].filter(Boolean).join('&'),
        ),
        method: HttpMethod.GET,
      }),
      transformResponse: (response: SchemaFieldDto[]) => response.map(fromApiSchemaField),
      keepUnusedDataFor: 0,
      providesTags: (_result, _error, { accountId }) => schemaFieldsTag(accountId),
    }),
    getSchemaFieldsV2: builder.query<ApiResponse<SchemaField[]>, GetSchemaFieldsV2Args>({
      query: ({ accountId, query, type, schemaType }) => ({
        url: buildEndpointWithQuery(
          `/v2/accounts/${accountId}/schema_fields`,
          [
            query,
            type ? `type=${encodeURIComponent(type)}` : '',
            schemaType ? `schema_type=${encodeURIComponent(schemaType)}` : '',
          ]
            .filter(Boolean)
            .join('&'),
        ),
        method: HttpMethod.GET,
      }),
      transformResponse: fromApiSchemaFieldList,
      providesTags: (_result, _error, { accountId }) => schemaFieldsTag(accountId),
    }),
    getSchemaFieldValues: builder.query<
      ApiResponse<SchemaFieldValueOption[]>,
      HttpBaseRequest<{ accountId: string; schemaFieldId: string; query?: string }>
    >({
      transformResponse: fromApiSchemaFieldValues,
      query: ({ accountId, schemaFieldId, query }) => ({
        url: buildEndpointWithQuery(`/v2/accounts/${accountId}/schema_fields/${schemaFieldId}/values`, query),
        method: HttpMethod.GET,
      }),
    }),
    getSchemaTagValues: builder.query<
      ApiResponse<SchemaFieldValueOption[]>,
      HttpBaseRequest<{ accountId: string; schemaFieldIds?: string[]; query?: string }>
    >({
      transformResponse: fromApiSchemaFieldValues,
      query: ({ accountId, schemaFieldIds, query }) => {
        const schemaFieldQuery = schemaFieldIds?.length ? `schema_field_ids=${schemaFieldIds.join(',')}` : '';
        const nextQuery = [query, schemaFieldQuery].filter(Boolean).join('&');

        return {
          url: buildEndpointWithQuery(`/v2/accounts/${accountId}/schema_fields/values`, nextQuery),
          method: HttpMethod.GET,
        };
      },
    }),
    createSchemaField: builder.mutation<SchemaField, CreateSchemaFieldArgs>({
      query: (args) => ({
        url: `/v1/accounts/${args.accountId}/schema_fields`,
        method: HttpMethod.POST,
        data: toApiSaveSchemaField(args.values),
      }),
      transformResponse: fromApiSchemaField,
      invalidatesTags: (_result, error, { accountId }) => (error ? [] : schemaFieldsTag(accountId)),
    }),
    updateSchemaField: builder.mutation<SchemaField, UpdateSchemaFieldArgs>({
      query: (args) => ({
        url: `/v1/accounts/${args.accountId}/schema_fields/${args.id}/update`,
        method: HttpMethod.POST,
        data: toApiSaveSchemaField(args.values),
      }),
      transformResponse: fromApiSchemaField,
      invalidatesTags: (_result, error, { accountId }) => (error ? [] : schemaFieldsTag(accountId)),
    }),
    toggleSchemaFieldArchive: builder.mutation<void, ToggleSchemaFieldArchiveArgs>({
      query: ({ accountId, id }) => ({
        url: `/v1/accounts/${accountId}/schema_fields/${id}/archive`,
        method: HttpMethod.POST,
      }),
      invalidatesTags: (_result, error, { accountId }) => (error ? [] : schemaFieldsTag(accountId)),
    }),
  }),
});

export const {
  useGetSchemaFieldsQuery,
  useGetSchemaFieldsV2Query,
  useLazyGetSchemaFieldsV2Query,
  useGetSchemaFieldValuesQuery,
  useLazyGetSchemaTagValuesQuery,
  useLazyGetSchemaFieldsQuery,
  useLazyGetSchemaFieldValuesQuery,
  useCreateSchemaFieldMutation,
  useUpdateSchemaFieldMutation,
  useToggleSchemaFieldArchiveMutation,
} = SchemaFieldsService;
